/**
 * The pages that hand a video back as an MP4 (SEO_BRIEF_11 section 2.1): /video-compressor and
 * /mp4-converter; and /mp3-to-video (SEO_BRIEF_12 section 4), an audio file and an optional
 * picture made into one.
 *
 * Each is a tool row like any other, so the hub, the sitemap and the link graph find it, and each
 * renders `tools/video_out.html` (server/app/routes_pages.py `VIDEO_OUT_SLUGS`) instead of the
 * drop-options-download shell, because nothing on it is a subtitle file:
 *
 *   video-compressor   the video re-encoded as H.264 with AAC sound, at a size the visitor picks:
 *                      Smaller, Much smaller, or under a size in MB (src/media/video-plan.ts).
 *   mp4-converter      the video repackaged as MP4 when it is H.264 with AAC already, otherwise
 *                      re-encoded to H.264 and AAC where it is not.
 *   mp3-to-video       an audio file with a still picture (the visitor's, or the file's name on
 *                      the site's dark background) as H.264 at 2 frames a second, with the sound
 *                      in the MP4 (src/media/still-plan.ts). Its own template,
 *                      `tools/still_video.html` (server/app/video_pages.py `STILL_SLUGS`).
 *
 * The work is web/static/burn (the /burn-subtitles engine), in the visitor's tab. `input: 'video'`
 * says the drop zone asks for a video; it is not a transcribing mode, so the row counts among the
 * tools that run in the browser (`runs_in_browser` in server/app/tools.py).
 *
 * The formats are FILE TYPES, as on the audio-out rows: `read_formats` is what the engine opens
 * and the page names, `write_formats` what Save hands back. Checked with a scripted run of the
 * engine on a file of each (src/media/video-out.ts `probeVideo`, 2026-11-02): MP4, M4V, MOV, MKV
 * and WebM open with their picture and sound found. Not named: 3GP opens, but an old phone's
 * H.263 picture and AMR sound are codecs no browser decodes, so only a 3GP in H.264 works;
 * MPEG-TS opens with its length misread; AVI, FLV, WMV and MPEG-PS don't open at all. The drop
 * zone still takes a 3GP or a TS (a phone that recorded H.264 into one gets its MP4), and the
 * page says plainly when one can't be read. `build/tools_export.mjs` carries the rows into
 * `tools.json` like any other channel's.
 */

import type { OptionsSchema } from '../transforms/clean/schema.ts';
import { AUDIO_READS } from './audio-convert-tools.ts';

/** The videos the drop zone names. */
export const VIDEO_OUT_READS = ['mp4', 'mov', 'mkv', 'webm', 'm4v'] as const;

/** Every page here saves MP4 and nothing else. */
export const MP4_WRITES = ['mp4'] as const;

/**
 * /mp3-to-video's audio files: the ones /audio-converter reads, in the order its page names them
 * (MP3, M4A, WAV, OGG, Opus, FLAC, AAC). MP3, M4A and WAV open in every browser the page runs in;
 * the others need the browser to play their sound, and the page says so when it can't.
 */
export const STILL_READS = AUDIO_READS;

export interface VideoOutRow {
  readonly slug: string;
  readonly label: string;
  readonly op: 'convert';
  readonly ops: readonly string[];
  readonly options_schema: OptionsSchema;
  readonly read_formats: readonly string[];
  readonly write_formats: readonly string[];
  readonly input: 'video';
}

/** The page draws its own controls (tools/video_out.html), so the shell's panel has none. */
const NO_OPTIONS: OptionsSchema = { version: 1, groups: [] };
const NO_OPS: readonly string[] = [];

export const VIDEO_OUT_TOOLS: readonly VideoOutRow[] = [
  {
    slug: 'video-compressor',
    label: 'Video compressor',
    op: 'convert',
    ops: NO_OPS,
    options_schema: NO_OPTIONS,
    read_formats: VIDEO_OUT_READS,
    write_formats: MP4_WRITES,
    input: 'video',
  },
  {
    slug: 'mp4-converter',
    label: 'MP4 converter',
    op: 'convert',
    ops: NO_OPS,
    options_schema: NO_OPTIONS,
    read_formats: VIDEO_OUT_READS,
    write_formats: MP4_WRITES,
    input: 'video',
  },
  {
    slug: 'mp3-to-video',
    label: 'MP3 to video',
    op: 'convert',
    ops: NO_OPS,
    options_schema: NO_OPTIONS,
    read_formats: STILL_READS,
    write_formats: MP4_WRITES,
    input: 'video',
  },
];
